import { useState, useRef, useEffect } from 'react';
import { useIsMobile } from 'app/utils/hooks';
import { ModalContentProps, ModalProps } from '@chakra-ui/react';
import { useMobileScreen } from './useMobileScreen';

export const useModalProps = (
  open: boolean,
  defaultContentHeight: string = '-webkit-fill-available'
): { modalProps: Partial<ModalProps>; contentProps: ModalContentProps } => {
  const isMobile = useIsMobile();
  const [maxHeight, setMaxHeight] = useState<string>(defaultContentHeight);

  const margin = useRef<number | 'auto'>('auto');

  const { isKeyboardOpen, screenOrientation } = useMobileScreen();

  useEffect(() => {
    if (isMobile && open) {
      const padding = 64;
      margin.current = isKeyboardOpen ? 0 : 'auto';
      if (isKeyboardOpen && window.visualViewport) {
        const height =
          window.visualViewport.height * window.visualViewport.scale;
        setMaxHeight(`${height - padding}px`);
      } else {
        setMaxHeight(defaultContentHeight);
      }

      return () => {
        margin.current = 'auto';
        setMaxHeight(defaultContentHeight);
      };
    }
    return () => {};
  }, [defaultContentHeight, isKeyboardOpen, isMobile, open, screenOrientation]);

  useEffect(() => {
    if (isKeyboardOpen) {
      document.activeElement?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [isKeyboardOpen, maxHeight]);

  return {
    modalProps: isMobile
      ? {
          scrollBehavior: 'inside',
          autoFocus: false,
        }
      : {},
    contentProps: isMobile
      ? {
          maxHeight,
          m: margin.current,
          p: 0,
          overflow: 'auto',
          overscrollBehavior: 'none',
          sx: {
            form: {
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }
      : {},
  };
};
